import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_ORDERS_API ?? '/api/v1';

export type OrderSource = 'amazon_spapi' | 'flipkart' | 'myntra' | 'ajio' | 'shopify' | 'woocommerce' | 'manual';

export type Order = {
  id: string;
  order_number: string;
  marketplace_order_id?: string;
  source: OrderSource;
  status: string;
  fulfilment_status: string;
  payment_status: string;
  allocation_status: string;
  shipment_status: string;
  currency: string;
  total_amount: number;
  health_score: number;
  sla_breached: boolean;
  created_at: string;
  updated_at: string;
};

export type OrderItem = {
  id: string;
  product_id: string;
  sku?: string;
  title: string;
  quantity: number;
  allocated_qty: number;
  unit_price: number;
  total_price: number;
};

export type OrderDashboard = {
  total_orders: number;
  pending_orders: number;
  allocated_orders: number;
  shipped_orders: number;
  on_hold_orders: number;
  sla_breached: number;
  avg_health_score: number;
  by_source: Record<string, number>;
  by_status: Record<string, number>;
};

export type OrderTimelineEntry = {
  id: string;
  status: string;
  message?: string;
  recorded_at: string;
};

export type OrderIntelligence = {
  order_id: string;
  health_score: number;
  fraud_risk_score: number;
  sla_prediction: string;
  delay_risk: number;
  recommendations: { type: string; score: number; suggestion: string }[];
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const ordersApi = {
  dashboard: () => request<OrderDashboard>('/orders/dashboard'),
  list: (params?: { source?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.source) q.set('source', params.source);
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    return request<Order[]>(`/orders${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<{ order: Order; items: OrderItem[] }>(`/orders/${id}`),
  import: (body: Record<string, unknown>) => request<Order>('/orders/import', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Record<string, unknown>) => request<Order>(`/orders/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  allocate: (id: string, body?: Record<string, unknown>) => request<Order>(`/orders/${id}/allocate`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  hold: (id: string, reason?: string) => request<Order>(`/orders/${id}/hold`, { method: 'POST', body: JSON.stringify({ reason }) }),
  resume: (id: string) => request<Order>(`/orders/${id}/resume`, { method: 'POST' }),
  cancel: (id: string, reason?: string) => request<Order>(`/orders/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  split: (id: string, body: Record<string, unknown>) => request<Order>(`/orders/${id}/split`, { method: 'POST', body: JSON.stringify(body) }),
  merge: (id: string, sourceOrderId: string) => request<Order>(`/orders/${id}/merge`, { method: 'POST', body: JSON.stringify({ source_order_id: sourceOrderId }) }),
  ship: (id: string, body?: Record<string, unknown>) => request<Order>(`/orders/${id}/ship`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  search: (q: string) => request<Order[]>(`/orders/search?q=${encodeURIComponent(q)}`),
  timeline: (id: string) => request<OrderTimelineEntry[]>(`/orders/${id}/timeline`),
  intelligence: (id: string) => request<OrderIntelligence>(`/orders/${id}/intelligence`),
};
